import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { FileText, Loader2, ArrowRight } from "lucide-react";

/**
 * AdminQueueBanner
 * ----------------
 * Thin banner rendered near the top of the admin app shell. Visible ONLY
 * when a report-analysis worker is currently active OR the pending queue
 * is non-empty. Polls the same /api/admin/reports/queue-status endpoint
 * that powers the System Status card, but at a lower frequency (15 s)
 * and stops when the queue drains. Non-admin users never see this.
 *
 * We intentionally keep the markup compact so it can live above every
 * admin route without consuming much vertical real estate.
 */
const fmtEta = (minutes) => {
  if (minutes == null) return null;
  if (minutes < 1) return "< 1 min";
  if (minutes < 60) return `~${Math.round(minutes)} min`;
  const h = Math.floor(minutes / 60);
  const rem = Math.round(minutes % 60);
  return `~${h}h ${rem}m`;
};

const AdminQueueBanner = () => {
  const { isAdmin } = useAuth();
  const [snap, setSnap] = useState(null);
  const pollRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const s = await apiGet("/admin/reports/queue-status");
      setSnap(s);
      return s;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return undefined;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      const s = await fetchStatus();
      const isActive = !!(s && (s.currently_analysing || (s.queue_length ?? 0) > 0));
      if (isActive && !cancelled) {
        pollRef.current = setTimeout(tick, 15000);
      }
    };
    tick();
    return () => {
      cancelled = true;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [isAdmin, fetchStatus]);

  if (!isAdmin) return null;
  const curr = snap?.currently_analysing;
  const queueLen = snap?.queue_length ?? 0;
  const active = !!(curr || queueLen > 0);
  if (!active) return null;
  const eta = fmtEta(snap?.estimated_total_minutes);
  return (
    <div
      className="flex items-center justify-between gap-3 rounded-md border border-[hsl(var(--info))]/40 bg-[hsl(var(--info))]/8 px-3 py-2 text-xs"
      data-testid="admin-queue-banner"
    >
      <div className="flex items-center gap-2 flex-wrap min-w-0">
        <FileText className="h-3.5 w-3.5 text-[hsl(var(--info))] shrink-0" />
        <span className="font-semibold text-foreground shrink-0">
          Analysing reports:
        </span>
        <span className="text-muted-foreground">
          {queueLen} queued
        </span>
        {curr && (
          <span className="text-muted-foreground inline-flex items-center gap-1 truncate">
            <span>· Current:</span>
            <span
              className="text-foreground font-medium truncate max-w-[320px]"
              title={`${curr.company} — ${curr.report_type || ""} ${curr.period || ""}`}
              data-testid="admin-queue-banner-current"
            >
              {curr.company} · {curr.report_type || "report"} {curr.period || ""}
            </span>
            <Loader2 className="h-3 w-3 animate-spin text-[hsl(var(--info))]" />
          </span>
        )}
        {eta && (
          <span className="text-muted-foreground">· Est: {eta}</span>
        )}
      </div>
      <Link
        to="/system-status"
        className="inline-flex items-center gap-1 font-semibold text-[hsl(var(--info))] hover:underline shrink-0"
        data-testid="admin-queue-banner-view-link"
      >
        View Queue
        <ArrowRight className="h-3 w-3" />
      </Link>
    </div>
  );
};

export default AdminQueueBanner;
